/** Display helpers for the notifications hub. Pure so node tests can load them. */

export function partialVin(vin) {
	if (vin == null) return '';
	const clean = String(vin).trim();
	if (!clean) return '';
	if (clean.length <= 6) return clean;
	return clean.slice(-6);
}

export function vehicleSummary(note) {
	const title = [note.year, note.make, note.model].filter(Boolean).join(' ');
	const vin = partialVin(note.vin);
	const vinLabel = vin ? `(...${vin})` : '';
	return [title, note.stock, vinLabel].filter(Boolean).join(' ');
}

const BADGE_LABELS = {
	ready: 'Ready',
	new: 'New',
	overdue: 'Overdue',
	returned: 'Returned',
};

/** Title-case a step name without changing letters that are already capitals ("Write RO"). */
export function displayStep(value) {
	return String(value || '').replace(/\b[a-z]/g, letter => letter.toUpperCase());
}

export function badgeLabel(badge) {
	const raw = String(badge || '').trim();
	if (!raw) return '';
	const known = BADGE_LABELS[raw.toLowerCase()];
	return known || displayStep(raw);
}

/** Ready/Returned are from → to. New is "Starting in". Overdue is "Still in". */
export function statusParts(note) {
	const badge = String(note.badge || '').toLowerCase();
	const status = note.status || '';
	const split = status.split(/\s*(?:→|->)\s*/);
	if (split.length === 2 && split[0] && split[1]) {
		return { kind: 'transition', from: displayStep(split[0]), to: displayStep(split[1]) };
	}
	const origin = note.originStep || '';
	const current = note.currentStep || '';
	if ((badge === 'ready' || badge === 'returned') && origin && current) {
		return { kind: 'transition', from: displayStep(origin), to: displayStep(current) };
	}
	if (badge === 'new' || /^starting in\b/i.test(status)) {
		const step = current || status.replace(/^starting in\s*/i, '') || status;
		return { kind: 'phrase', lead: 'Starting in', step: displayStep(step) };
	}
	if (badge === 'overdue' || /^still in\b/i.test(status)) {
		const step = current || status.replace(/^still in\s*/i, '') || status;
		return { kind: 'phrase', lead: 'Still in', step: displayStep(step) };
	}
	if (origin && current) {
		return { kind: 'transition', from: displayStep(origin), to: displayStep(current) };
	}
	return { kind: 'text', text: displayStep(status) };
}

/**
 * Relative time matching the hub copy: "5 min ago", "2 hrs ago".
 * `now` is epoch ms so tests can freeze the clock.
 */
export function relativeTime(iso, now = Date.now()) {
	if (!iso) return '';
	const then = new Date(iso).getTime();
	if (Number.isNaN(then)) return '';
	const seconds = Math.max(0, Math.round((now - then) / 1000));
	if (seconds < 45) return 'just now';
	const minutes = Math.round(seconds / 60);
	if (minutes < 60) return minutes === 1 ? '1 min ago' : `${minutes} min ago`;
	const hours = Math.round(minutes / 60);
	if (hours < 24) return hours === 1 ? '1 hr ago' : `${hours} hrs ago`;
	const days = Math.round(hours / 24);
	if (days < 30) return days === 1 ? '1 day ago' : `${days} days ago`;
	return new Date(then).toLocaleDateString();
}

function pick(raw, keys) {
	for (let i = 0; i < keys.length; i += 1) {
		const value = raw[keys[i]];
		if (value != null && value !== '') return value;
	}
	return '';
}

export function normalizeNotification(raw) {
	if (!raw || typeof raw !== 'object') return null;
	const attrs = raw.attributes && typeof raw.attributes === 'object'
		? { ...raw.attributes, id: raw.id != null ? raw.id : raw.attributes.id }
		: raw;
	if (attrs.id == null) return null;
	const readAt = attrs.read_at || attrs.readAt || null;
	const read = attrs.read === true || attrs.read === 'true' || !!readAt;
	return {
		id: attrs.id,
		read,
		badge: pick(attrs, ['badge', 'kind']),
		status: pick(attrs, ['status', 'status_line', 'statusLine', 'summary', 'message']),
		storeName: pick(attrs, ['store_name', 'storeName', 'dealership_name']),
		subdomain: pick(attrs, ['subdomain', 'dealership']),
		database: pick(attrs, ['database']),
		dealershipId: attrs.dealership_id || attrs.dealershipId || null,
		vehicleId: attrs.vehicle_id != null ? attrs.vehicle_id : (attrs.vehicleId != null ? attrs.vehicleId : null),
		departmentId: attrs.department_id != null ? attrs.department_id : (attrs.departmentId != null ? attrs.departmentId : null),
		year: attrs.year || '',
		make: attrs.make || '',
		model: attrs.model || '',
		stock: attrs.stock || '',
		vin: pick(attrs, ['vin', 'partial_vin', 'partialVin']),
		originStep: pick(attrs, ['origin_step', 'originStep', 'from_step', 'fromStep']),
		currentStep: pick(attrs, ['current_step', 'currentStep', 'to_step', 'toStep']),
		createdAt: attrs.created_at || attrs.createdAt || attrs.occurred_at || attrs.occurredAt || attrs.notified_at || attrs.notifiedAt || null,
	};
}

export function normalizeInbox(body) {
	const root = body && typeof body === 'object' ? body : {};
	let payload = root;
	if (!root.notifications && root.data && !Array.isArray(root.data) && root.data.notifications) {
		payload = root.data;
	}
	let list = payload.notifications;
	if (!list && Array.isArray(payload.data)) list = payload.data;
	if (!list && Array.isArray(root.data)) list = root.data;
	const notifications = (Array.isArray(list) ? list : [])
		.map(normalizeNotification)
		.filter(Boolean);
	let unreadCount = payload.unread_count;
	if (unreadCount == null && root.meta) unreadCount = root.meta.unread_count;
	if (unreadCount == null && payload.meta) unreadCount = payload.meta.unread_count;
	if (unreadCount == null) unreadCount = notifications.filter(note => !note.read).length;
	const count = Number(unreadCount);
	return {
		unreadCount: Number.isFinite(count) ? count : 0,
		notifications,
	};
}
